// src/offlineQueue.js
// localStorage-based offline write queue for NutriLithe and LiftLithe.
//
// When navigator.onLine is false, Supabase writes are buffered here instead
// of failing silently. When connectivity is restored the caller must call
// processQueue(supabase) — App.js and WorkoutApp.jsx both listen for the
// window "online" event and do this automatically.
//
// Queue entry shape:
//   {
//     id:        string  (unique, prefixed "oq_")
//     timestamp: ISO string
//     table:     string  (Supabase table name)
//     operation: "insert" | "update" | "upsert" | "delete"
//     payload:   object  (row data)
//     retries:   number  (incremented on failure; dropped after MAX_RETRIES)
//     userId:    string  (auth uid, for reference only)
//   }

const QUEUE_KEY      = "lithe_offline_queue";
const MAX_RETRIES    = 3;
const MAX_QUEUE_SIZE = 100;

function _uuid() {
  return "oq_" + Date.now().toString(36) + Math.random().toString(36).slice(2, 9);
}

function _read() {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]");
  } catch (_) {
    return [];
  }
}

function _write(queue) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
    return true;
  } catch (_) {
    return false;
  }
}

// S109: Table whitelist — only known app tables can be queued
const VALID_TABLES = [
  "logs", "water_logs", "weight_logs", "body_measurements",
  "saved_meals", "food_suggestions", "supplement_logs", "supplement_stacks",
  "workout_sessions", "workout_routines", "workout_programmes", "profiles",
];
const MAX_PAYLOAD_BYTES = 10_000; // 10 KB

// ─── enqueue ──────────────────────────────────────────────────────────────────
// Add a pending write to the queue.
// Returns { queued: true } on success or { queued: false, reason } on failure.
export function enqueue(table, operation, payload, userId) {
  // S109: Validate table name against whitelist
  if (!VALID_TABLES.includes(table)) {
    console.warn("[offlineQueue] Blocked unknown table:", table);
    return { queued: false, reason: "invalid_table" };
  }
  // S109: Reject oversized payloads
  try {
    if (JSON.stringify(payload).length > MAX_PAYLOAD_BYTES) {
      console.warn("[offlineQueue] Payload exceeds 10KB limit for table:", table);
      return { queued: false, reason: "payload_too_large" };
    }
  } catch (_) {
    return { queued: false, reason: "payload_serialize_failed" };
  }
  const q = _read();
  if (q.length >= MAX_QUEUE_SIZE) {
    console.warn(`[offlineQueue] Queue full (${q.length}/${MAX_QUEUE_SIZE}). Item NOT queued:`, table, operation);
    return { queued: false, reason: "queue_full" };
  }
  q.push({
    id:        _uuid(),
    timestamp: new Date().toISOString(),
    table,
    operation,
    payload,
    retries:   0,
    userId:    userId ?? null,
  });
  const written = _write(q);
  if (!written) {
    console.warn("[offlineQueue] localStorage write failed. Item NOT queued:", table, operation);
    return { queued: false, reason: "storage_write_failed" };
  }
  return { queued: true };
}

// ─── processQueue ─────────────────────────────────────────────────────────────
// Attempt to flush all queued items to Supabase.
// Returns { processed: number, failed: number, remaining: number }
export async function processQueue(supabase) {
  const q = _read();
  if (q.length === 0) return { processed: 0, failed: 0, remaining: 0 };

  let processed = 0;
  let failed    = 0;
  const remaining = [];

  for (const item of q) {
    try {
      let error;
      const { table, operation, payload } = item;

      if (operation === "insert") {
        ({ error } = await supabase.from(table).insert(payload));
      } else if (operation === "upsert") {
        ({ error } = await supabase.from(table).upsert(payload));
      } else if (operation === "update") {
        const { id, ...fields } = payload;
        ({ error } = await supabase.from(table).update(fields).eq("id", id));
      } else if (operation === "delete") {
        ({ error } = await supabase.from(table).delete().eq("id", payload.id));
      }

      if (error) throw new Error(error.message);
      processed++;
    } catch (_) {
      const newRetries = (item.retries ?? 0) + 1;
      if (newRetries <= MAX_RETRIES) {
        remaining.push({ ...item, retries: newRetries });
      } else {
        failed++; // Permanently drop after MAX_RETRIES failures
        try { window.dispatchEvent(new CustomEvent("offlineQueueItemDropped", { detail: { table: item.table, operation: item.operation } })); } catch(_) {}
      }
    }
  }

  _write(remaining);
  return { processed, failed, remaining: remaining.length };
}

// ─── getQueueLength ───────────────────────────────────────────────────────────
// Returns the number of items currently in the queue.
export function getQueueLength() {
  return _read().length;
}

// ─── isQueueFull ──────────────────────────────────────────────────────────────
// Returns true when the queue has reached its maximum capacity.
export function isQueueFull() {
  return _read().length >= MAX_QUEUE_SIZE;
}

// ─── getQueueStats ────────────────────────────────────────────────────────────
// Returns summary stats about the current queue state.
export function getQueueStats() {
  const q = _read();
  return {
    length:     q.length,
    maxSize:    MAX_QUEUE_SIZE,
    isFull:     q.length >= MAX_QUEUE_SIZE,
    oldestItem: q.length > 0 ? q[0].timestamp : null,
  };
}

// ─── clearQueue ───────────────────────────────────────────────────────────────
// Empty the queue entirely (call on logout).
export function clearQueue() {
  _write([]);
}
